import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface ChatPanelProps {
  jobId: string;
  userId: string;
  userType: 'customer' | 'provider';
  jobStatus: string;
}

interface ChatMessage {
  id: string;
  sender_id: string;
  sender_type: string;
  message: string;
  attachments: string[];
  created_at: string;
  read_at: string | null;
}

export default function ChatPanel({ jobId, userId, userType, jobStatus }: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Chat is only active when job is accepted or in_progress
  const isChatActive = jobStatus === 'accepted' || jobStatus === 'in_progress';

  useEffect(() => {
    loadMessages();
    
    // Subscribe to new messages
    const channel = supabase
      .channel(`chat-${jobId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `job_id=eq.${jobId}`
      }, (payload) => {
        setMessages(prev => [...prev, payload.new as ChatMessage]);
        scrollToBottom();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [jobId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('job_id', jobId)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data);
    }
    setLoading(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !jobId) return;

    // Check if chat is active before sending
    if (!isChatActive) {
      setError('Chat is only available during active jobs (accepted or in progress).');
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Session expired. Please sign in again.');
        setSending(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-chat-message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            jobId,
            senderId: user.id,
            message: messageText,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        
        if (errorData.error?.includes('not found')) {
          setError('Job not found. The chat may no longer be available.');
        } else if (errorData.error?.includes('permission')) {
          setError('You do not have permission to send messages in this chat.');
        } else {
          setError('Unable to send message. Please try again.');
        }
        
        // Restore message if send failed
        setNewMessage(messageText);
        setSending(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        // Message sent successfully - it will appear via real-time subscription
        setSending(false);
      } else {
        setError('Message failed to send. Please try again.');
        setNewMessage(messageText);
        setSending(false);
      }
    } catch (err) {
      console.error('Send message error:', err);
      setError('Connection error. Please check your internet and try again.');
      setNewMessage(messageText);
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg flex flex-col h-[600px]">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <i className="ri-message-3-line text-teal-600"></i>
          Chat with {userType === 'customer' ? 'Provider' : 'Customer'}
        </h2>
        {!isChatActive && (
          <p className="text-sm text-amber-600 mt-2 flex items-center gap-2">
            <i className="ri-information-line"></i>
            Chat is only available during active jobs
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {error && (
          <div className="px-4 py-2 bg-red-50 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="text-red-500 hover:text-red-700"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mr-3"></div>
            <p className="text-gray-600">Loading messages...</p>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <i className="ri-chat-3-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Messages Yet</h3>
            <p className="text-gray-600 text-sm">
              {isChatActive 
                ? 'Start the conversation by sending a message below.'
                : 'Chat will be available once the job is accepted.'}
            </p>
          </div>
        )}

        {messages.map((msg) => {
          const isOwnMessage = msg.sender_id === userId;
          return (
            <div
              key={msg.id}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                  isOwnMessage
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="text-sm">{msg.message}</p>
                <p className={`text-xs mt-1 ${isOwnMessage ? 'text-teal-100' : 'text-gray-500'}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {isChatActive ? (
        <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="p-6 border-t border-gray-200">
          <div className="flex gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-sm"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center whitespace-nowrap"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill mr-2"></i>
                  Send
                </>
              )}
            </button>
          </div>
        </form>
      ) : (
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-center text-gray-500">
            <i className="ri-lock-line text-2xl mb-2"></i>
            <p className="text-sm">
              {jobStatus === 'completed' 
                ? 'Chat closed - Job completed' 
                : jobStatus === 'cancelled'
                ? 'Chat closed - Job cancelled'
                : 'Chat will be available once the job is accepted'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}